import { useState } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { FloatLabel } from "primereact/floatlabel";
import { Divider } from "primereact/divider";
import classes from "./RuleWizard.module.css";

const conditionPinStateLabel = (cond, globalIsLock, isTimeTrigger, triggerTime, globalPinStatusOn, globalPinStatusOff, timeframeMs) => {
    const isLock = cond.is_lock ?? globalIsLock;
    const isAbsence = cond.is_absence_condition;
    const isDeadline = cond.is_deadline_condition;
    const onLabel   = isLock ? "closed" : (cond.pin_status_on  || globalPinStatusOn  || "armed");
    const offLabel  = isLock ? "open"   : (cond.pin_status_off || globalPinStatusOff || "disarmed");
    const timeSuffix = isTimeTrigger && triggerTime ? ` at ${triggerTime.slice(0, 5)}` : "";
    const absenceTimeSuffix = ` within ${timeframeMs > 0 ? formatTempDuration(timeframeMs) : "... time"}`;
    switch (cond.pin_state) {
        case "ARMED":    return isAbsence ? `is NOT ${onLabel} ${absenceTimeSuffix}` : isDeadline ? `is already ${onLabel}` : isTimeTrigger ? `is still ${onLabel}${timeSuffix}` : `changes status to ${onLabel}`;
        case "DISARMED": return isAbsence ? `is NOT ${offLabel} ${absenceTimeSuffix}` : isDeadline ? `is already ${offLabel}` : isTimeTrigger ? `is still ${offLabel}${timeSuffix}` : `changes status to ${offLabel}`;
        case "ANY":      return isTimeTrigger ? `is ${onLabel} or ${offLabel} (toggled)${timeSuffix}` : `changes status to ${onLabel} or ${offLabel} (toggled)`;
        case "PULSE":    return `is pulsed${timeSuffix}`;
        default:         return `is ${cond.pin_state || "?"}${timeSuffix}`;
    }
};

const GATEWAY_EVENT_PHRASES = {
    unlocked: "a correct pin entry",
    opened: "a padlock open",
    locked: "a padlock close",
    invalidPIN: "an invalid pin attempt",
};

const gatewayConditionLabel = (cond) => {
    const phrase = GATEWAY_EVENT_PHRASES[cond.gateway_event_subtype] || cond.pin_name;
    return `When ${phrase} is reported by the gateway`;
};

const DAY_NAMES = ["Sun", "Mon", "Tues", "Wed", "Thur", "Fri", "Sat"];

const convertDayLabels = (days) => {
    const sorted = [...days].map(Number).sort((a, b) => a - b);

    if (sorted.length === 7) return "everyday";

    const isWeekday = (d) => d >= 1 && d <= 5;
    const isWeekend = (d) => d === 0 || d === 6;

    if (sorted.length === 5 && sorted.every(isWeekday)) return "weekdays";
    if (sorted.length === 2 && sorted.every(isWeekend)) return "weekends";

    return sorted.map(d => DAY_NAMES[d]).join(", ");
};

const getRestrictionLabel = (state) => {
    const days = state.days_of_week || [];
    const hasDayRestriction = days.length > 0 && days.length < 7;
    const hasTimeRestriction = state.time_window_start_local != null && state.time_window_end_local != null;
    if (!hasDayRestriction && !hasTimeRestriction) return null;

    const timeText = hasTimeRestriction ? `between ${state.time_window_start_local} and ${state.time_window_end_local}` : "";
    const daysText = hasDayRestriction ? `on ${convertDayLabels(days)}` : "";
    const phrase = [timeText, daysText].filter(Boolean).join(" ");
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
};

const controlTypeLabel = (ct) => {
    const map = { ARM: "arm", TEMP_ARM: "temporarily arm", DISARM: "disarm", TEMP_DISARM: "temporarily disarm", OPPOSE: "oppose", TOGGLE: "toggle", MATCH: "match", PULSE: "pulse" };
    return map[ct] ?? ct?.toLowerCase() ?? "—";
};

const formatTempDuration = (ms) => (ms % 60000 === 0 ? `${ms / 60000} minute${ms === 60000 ? "" : "s"}` : `${ms / 1000} seconds`);

const getTimeframeWindowLabel = (state) => {
    if (!state.timeframe_window_ms) return null;
    const duration = formatTempDuration(state.timeframe_window_ms);
    const hasDeadline = state.conditions.some(c => c.is_deadline_condition);
    const windowExplainer = state.timeframe_window_mode === "SLIDING"
        ? "restarting the clock every time one of them happens"
        : "starting from the moment the first one happens";
    return hasDeadline
        ? `Your conditions must all happen within ${duration} of each other, ${windowExplainer}. Once that window ends, the status checks are evaluated to decide if the rule fires.`
        : `Your conditions must all happen within ${duration} of each other, ${windowExplainer}.`;
};

const WizardStepRuleInfo = ({ onBack, onCreate }) => {
    const { state, dispatch } = useRule();
    const defaultName = state.isGeofence ? `${state.geofenceDirection} geofence rule` : (state.name || "");
    const [name, setName] = useState(defaultName);
    const [description, setDescription] = useState(state.description || "");
    const [creating, setCreating] = useState(false);

    const handleCreate = async () => {
        if (!name.trim()) return;
        dispatch({ type: "SET_RULE_NAME", payload: name });
        dispatch({ type: "SET_RULE_DESCRIPTION", payload: description });
        setCreating(true);
        await onCreate({ ...state, name, description });
        setCreating(false);
    };

    const controlActions = state.actions.filter(a => a.action_type === "CONTROL_OUTPUT");
    const hasAlert = state.actions.some(a => a.action_type === "SEND_ALERT");

    return (
        <>
            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>Rule info</h2>
                <p className={classes.contentSubtitle}>Give this rule a name and review the summary before creating it.</p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "18px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    <FloatLabel>
                        <InputText
                            id="wizard-rule-name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            style={{ width: "100%" }}
                        />
                        <label htmlFor="wizard-rule-name">Rule name *</label>
                    </FloatLabel>
                    <FloatLabel>
                        <InputTextarea
                            id="wizard-rule-desc"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={3}
                            style={{ width: "100%" }}
                        />
                        <label htmlFor="wizard-rule-desc">Description (optional)</label>
                    </FloatLabel>
                </div>

                <Divider />

                {/* Summary */}
                <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <p style={{ margin: 0, fontWeight: 700, color: "#6e6e6e", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Summary
                    </p>

                    {state.isTimeTrigger && (
                        <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>
                                Trigger:
                            </p>
                            <p style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>
                                At <strong>{state.triggerTime?.slice(0, 5)}</strong>
                                {state.triggerTimezone ? ` (${state.triggerTimezone})` : ""}
                            </p>
                        </div>
                    )}

                    {state.conditions.length > 0 && !state.isGeofence && (() => {
                        const triggerConditions = state.conditions.filter(c => !c.is_deadline_condition);
                        const deadlineConditions = state.conditions.filter(c => c.is_deadline_condition);
                        return (
                            <>
                                {triggerConditions.length > 0 && (
                                    <div>
                                        {triggerConditions.length > 1 && (
                                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>
                                                {`Conditions (${state.evaluation_mode === "SEQUENCE" ? "in sequence" : state.conditions_logic_operator}):`}
                                            </p>
                                        )}
                                        {triggerConditions.map((cond, i) => (
                                            <p key={i} style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>
                                                {cond.condition_type === "GATEWAY_EVENT"
                                                    ? `${i + 1}. ${gatewayConditionLabel(cond)}`
                                                    : <>{i + 1}. {state.isTimeTrigger ? "If" : "When"} <strong>{cond.pin_name ?? `Pin ${cond.pin}`}</strong> {conditionPinStateLabel(cond, state.isLock, state.isTimeTrigger, state.triggerTime, state.pinStatusOn, state.pinStatusOff, state.timeframe_window_ms)}</>}
                                            </p>
                                        ))}
                                    </div>
                                )}
                                {deadlineConditions.length > 0 && (
                                    <div style={{ marginTop: triggerConditions.length > 0 ? "8px" : 0 }}>
                                        <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>
                                            {`Status checks (${state.deadline_logic_operator}):`}
                                        </p>
                                        {deadlineConditions.map((cond, i) => (
                                            <p key={i} style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>
                                                {i + 1}. {state.isTimeTrigger ? "If" : "When"} <strong>{cond.pin_name ?? `Pin ${cond.pin}`}</strong> {conditionPinStateLabel(cond, state.isLock, state.isTimeTrigger, state.triggerTime, state.pinStatusOn, state.pinStatusOff, state.timeframe_window_ms)}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </>
                        );
                    })()}

                    {!state.isTimeTrigger && getRestrictionLabel(state) && (
                        <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>Restrictions:</p>
                            <p style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>{getRestrictionLabel(state)}</p>
                        </div>
                    )}

                    {getTimeframeWindowLabel(state) && (
                        <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>Timeframe:</p>
                            <p style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>{getTimeframeWindowLabel(state)}</p>
                        </div>
                    )}

                    {(controlActions.length > 0 || hasAlert) && (
                        <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>Actions:</p>
                            {hasAlert && (
                                <p style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>🔔 Send alert notification</p>
                            )}
                            {state.selectedPinsToAffect.map((pin, i) => {
                                const action = state.actions.find(a => a.site_to === pin.DeviceUUID && a.target_output === String(pin.Pin));
                                return (
                                    <p key={i} style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>
                                        {action?.delay_seconds > 0 ? <span style={{ color: "#9e9e9e" }}>After {action.delay_seconds}s — </span> : null}
                                        <strong>{pin.pinName}</strong> will {controlTypeLabel(action?.control_type)}
                                        {(action?.temporary_time_ms > 0) ? ` for ${formatTempDuration(action.temporary_time_ms)}` : ""}
                                    </p>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} disabled={creating} />
                <Button
                    label={creating ? "Creating..." : "Create rule"}
                    icon={creating ? "pi pi-spin pi-spinner" : "pi pi-check"}
                    iconPos="right"
                    severity="success"
                    disabled={!name.trim() || creating}
                    onClick={handleCreate}
                />
            </div>
        </>
    );
};

export default WizardStepRuleInfo;
